import { BankAccountHolderType, CreditCardMetaData, InvoiceStatus, NameCodePair, PaymentProcessorDisplayItem, PaymentSourceDisplayItem, PaymentSourceInputItem, PaymentSourceType, PaymentSurchargeDisplayItem, ProcessPaymentSourceResultItem, SignupResultItem, SurchargeResultItem } from '@sb/library/data-models';
import { ConfirmPaymentData, StripeElements, StripeError } from '@stripe/stripe-js';
import { Observable } from 'rxjs';
import { Field, Scope } from '../base';
import { ItemWithID } from './base.definitions';

export const AccountTypeName: Record<BankAccountHolderType, string> = {
    Personal: $localize`:@@SPAddPaymentSourceBankAccountTypePersonal:Personal`,
    Business: $localize`:@@SPAddPaymentSourceBankAccountTypeBusiness:Business`
}

export class CreditCardType extends CreditCardMetaData implements ItemWithID {
    ID!: string;

    constructor(initializer: CreditCardType) {
        super();
        Object.assign(this, initializer);
    }
}

export class BankAccountType implements ItemWithID {
    ID!: string;
    Type!: BankAccountHolderType;
    Name!: string;

    constructor(initializer: BankAccountType) {
        Object.assign(this, initializer);
    }
}

export const BankAccountTypes: BankAccountType[] = [
    new BankAccountType({
        ID: 'business',
        Type: BankAccountHolderType.Business,
        Name: $localize`:@@SPAddPaymentSourceBankAccountTypeBusiness:Business`
    }),
    new BankAccountType({
        ID: 'personal',
        Type: BankAccountHolderType.Personal,
        Name: $localize`:@@SPAddPaymentSourceBankAccountTypePersonal:Personal`
    })
];

export class NameCodeWithID extends NameCodePair implements ItemWithID {
    ID!: string;

    constructor(initializer: NameCodeWithID) {
        super();
        Object.assign(this, initializer);
    }
}

export class PaymentSchoolInfo {
    ID!: string;
    Name: string | null = null;
    Address: string | null = null;
    Active: boolean = true;
    InvoiceLogoURL: string | null = null;

    PaymentSurchargeCreditCard: PaymentSurchargeDisplayItem | null = null;
    PaymentSurchargeACH: PaymentSurchargeDisplayItem | null = null;
    AllowPartialPayments: boolean = false;

    CurrencyCode!: string;
    ForceAutoPay!: boolean;

    constructor(initializer: Partial<PaymentSchoolInfo>) {
        Object.assign(this, initializer);
    }
}

export class PaymentDetails {
    FamilyID: string | null = null;
    ParentID: string | null = null;

    FamilyName: string | null = null;
    InvoiceAmountDue: number | null = null;
    InvoiceStatus: InvoiceStatus = InvoiceStatus.Open;
    LateFee: number | null = null;

    constructor(initializer?: Partial<PaymentDetails>) {
        Object.assign(this, initializer);
    }
}

export enum PaymentSourceMode {
    Add = "Add",
    Payment = "Payment",
    AnonymousPayment = "AnonymousPayment"
}

export type StripeOptions = {
    elements: StripeElements;
    confirmParams?: Partial<ConfirmPaymentData> | undefined;
    redirect: "if_required";
};

export class StripeErrorDialogInfo {
    error!: StripeError;

    constructor(initializer: StripeErrorDialogInfo) {
        Object.assign(this, initializer);
    }
}

export enum PaymentContext {
    StudentPortal = "StudentPortal",
    PaymentPortal = "PaymentPortal",
    TeacherPortal = "TeacherPortal",
    SignupWidget = "SignupWidget"
}

export enum StripeRedirectStatus {
    Success = "succeeded",
    Failure = "failed"
}

export interface PaymentErrorDialogData {
    IsProcessorError: boolean;
    ErrorMessage: string;
}

class SharedPaymentScope extends Scope {
    context = Field<PaymentContext>();
    mode = Field<PaymentSourceMode>();
}

export class PaymentSourcesScope extends SharedPaymentScope {
    forceAutoPay = Field<boolean>();
    paymentSources? = Field<PaymentSourceDisplayItem[]>();
    triggerRefetchForParentID? = Field<Observable<string>>();
    AddPaymentSource? = Field<(parentID: string) => void>();
    PaymentSourceSelected? = Field<(paymentSource: PaymentSourceDisplayItem) => void>();
}

export class PaymentContextScope extends SharedPaymentScope {
    schoolInfo = Field<PaymentSchoolInfo>();
    paymentDetails = Field<PaymentDetails>();

    paymentProcessor = Field<PaymentProcessorDisplayItem | null>();

    FetchPaymentSourcesFailed? = Field<() => void>();
    PaymentSuccessful? = Field<() => void>();
    Dismissed? = Field<() => void>();
}

export class PaymentProcessingScope extends Scope {
    PaymentProcessorReady = Field<() => void>();
    PaymentSourceTypeChanged? = Field<(type: PaymentSourceType) => void>();

    triggerUpdateAmountWithSurcharge? = Field<Observable<SurchargeResultItem>>();
    triggerReenablePaymentProcessorForm = Field<Observable<void>>();

    triggerProcessPaymentSource = Field<Observable<void>>();
    ProcessPaymentSourceComplete = Field<(paymentSource: PaymentSourceInputItem) => void>();

    triggerProcessPaymentSourceNextStep = Field<Observable<ProcessPaymentSourceResultItem | SignupResultItem>>();
    PaymentSourceNextStepComplete = Field<(nextActionResult: string) => void>();
}
